import { toUserMessage } from '../../lib/errors.js';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { barcodeOutline, closeOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useActiveBusiness } from '../../lib/auth/useActiveBusiness.js';
import { useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import { scanBarcode } from '../../lib/barcode/scan.js';
import { useCelebrate } from '../CelebrationToast.js';
import { SearchPickerModal, PickerTriggerField } from '../SearchPickerModal.js';
import type { OperationalPersonnelRow } from '../../lib/auth/useOperationalIdentity.js';

export interface StorageUnitRecord {
  id: string;
  name: string;
  kind: string;
  temp_min_c: number | null;
  temp_max_c: number | null;
  business_id: string;
  version?: number;
  assigned_personnel_ids?: string[] | null;
  requires_photo?: boolean;
}

export interface TemperaturePointEditorModalProps {
  isOpen: boolean;
  initial?: StorageUnitRecord;
  initialName?: string;
  onDismiss: () => void;
  onSaved?: (saved: StorageUnitRecord) => void;
  onRequestArchive?: (rec: { id: string; name: string; version: number }) => void;
}

interface InternalFormState {
  id: string;
  name: string;
  kind: string;
  min: string;
  max: string;
  serial_number: string;
  humidity: string;
  version: number;
  assigned_personnel_ids: string[];
  requires_photo: boolean;
}

function buildInitialState(initial: StorageUnitRecord | undefined, initialName: string | undefined): InternalFormState {
  if (initial) {
    return {
      id: initial.id,
      name: initial.name,
      kind: initial.kind,
      min: initial.temp_min_c != null ? String(initial.temp_min_c) : '',
      max: initial.temp_max_c != null ? String(initial.temp_max_c) : '',
      serial_number: '',
      humidity: '',
      version: initial.version ?? 0,
      assigned_personnel_ids: initial.assigned_personnel_ids ?? [],
      requires_photo: initial.requires_photo ?? false,
    };
  }
  return {
    id: '',
    name: initialName ?? '',
    kind: 'frigo_positivo',
    min: '',
    max: '',
    serial_number: '',
    humidity: '',
    version: 0,
    assigned_personnel_ids: [],
    requires_photo: false,
  };
}

export function TemperaturePointEditorModal(props: TemperaturePointEditorModalProps): JSX.Element {
  const { isOpen, initial, initialName, onDismiss, onSaved, onRequestArchive } = props;
  const { profile } = useAuth();
  void profile;
  const createMaster = useEnqueueMaster();
  const activeBusiness = useActiveBusiness();
  const { data: personnel = [] } = useMasters<OperationalPersonnelRow>('tenant_personnel');

  const [form, setForm] = useState<InternalFormState>(() => buildInitialState(initial, initialName));
  const [toast, setToast] = useState<string | null>(null);
  const celebrate = useCelebrate();
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialState(initial, initialName));
    }
  }, [isOpen, initial, initialName]);

  async function scanSerial(): Promise<void> {
    try {
      const code = await scanBarcode();
      setForm((f) => ({ ...f, serial_number: code }));
      setToast(`Scansionato: ${code}`);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function save(): Promise<void> {
    const ctx = await activeBusiness.resolve();
    if (!ctx) {
      setToast('Nessun locale attivo');
      return;
    }
    const trimmed = form.name.trim();
    if (!trimmed) {
      setToast('Nome richiesto');
      return;
    }
    const minN = form.min ? Number(form.min.replace(',', '.')) : null;
    const maxN = form.max ? Number(form.max.replace(',', '.')) : null;
    const humN = form.humidity ? Number(form.humidity.replace(',', '.')) : null;
    const id = form.id || crypto.randomUUID();
    const isUpdate = !!form.id;
    try {
      await createMaster.mutateAsync({
        table: 'storage_units',
        id,
        op: isUpdate ? 'update' : 'insert',
        base_version: form.version ?? 0,
        patch: {
          id,
          tenant_id: ctx.tenantId,
          business_id: ctx.businessId,
          name: trimmed,
          kind: form.kind,
          temp_min_c: Number.isFinite(minN as number) ? minN : null,
          temp_max_c: Number.isFinite(maxN as number) ? maxN : null,
          check_frequency_hours: 24,
          active: true,
          serial_number: form.serial_number.trim() || null,
          humidity_pct: Number.isFinite(humN as number) && humN !== null && humN >= 0 && humN <= 100 ? humN : null,
          assigned_personnel_ids: form.assigned_personnel_ids,
          requires_photo: form.requires_photo,
        },
      });
      celebrate(isUpdate ? 'Frigorifero aggiornato' : 'Frigorifero aggiunto');
      onSaved?.({
        id,
        name: trimmed,
        kind: form.kind,
        temp_min_c: Number.isFinite(minN as number) ? minN : null,
        temp_max_c: Number.isFinite(maxN as number) ? maxN : null,
        business_id: ctx.businessId,
        version: (form.version ?? 0) + 1,
        assigned_personnel_ids: form.assigned_personnel_ids,
        requires_photo: form.requires_photo,
      });
      onDismiss();
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <>
      <IonModal data-testid="temp-point-editor" isOpen={isOpen} onDidDismiss={onDismiss}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{form.id ? 'Modifica frigorifero' : 'Nuovo frigorifero'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onDismiss} aria-label="chiudi">
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
          <div style={{ padding: '12px 16px 24px' }}>
            <h2 style={{ margin: '4px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              ❄️ Frigorifero
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Nome *</IonLabel>
                <IonInput
                  value={form.name}
                  placeholder="Frigo cucina"
                  onIonInput={(ev) => setForm((f) => ({ ...f, name: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🏷️ Tipo
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {([
                  { v: 'frigo_positivo', label: 'Frigo positivo' },
                  { v: 'frigo_negativo', label: 'Frigo negativo' },
                  { v: 'abbattitore',    label: 'Abbattitore' },
                  { v: 'cella_frigo',    label: 'Cella frigo' },
                  { v: 'vetrina',        label: 'Vetrina' },
                  { v: 'dispensa',       label: 'Dispensa' },
                  { v: 'altro',          label: 'Altro' },
                ] as const).map((k) => {
                  const active = form.kind === k.v;
                  return (
                    <button
                      key={k.v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, kind: k.v }))}
                      style={{
                        border: `1px solid ${active ? '#0066cc' : '#cbd5e1'}`,
                        background: active ? '#0066cc' : '#fff',
                        color: active ? '#fff' : '#475569',
                        borderRadius: 999,
                        padding: '5px 11px',
                        fontSize: 12,
                        fontWeight: active ? 600 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      {k.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🌡️ Range temperatura
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  placeholder="min °C"
                  value={form.min}
                  onIonInput={(ev) => setForm((f) => ({ ...f, min: String(ev.detail.value ?? '') }))}
                  style={{
                    flex: 1, fontSize: 14, fontVariantNumeric: 'tabular-nums',
                    '--padding-start': '10px', '--padding-end': '10px',
                    '--padding-top': '8px', '--padding-bottom': '8px',
                    '--background': '#eef2f7', '--border-radius': '10px',
                  } as React.CSSProperties}
                />
                <span style={{ color: '#94a3b8', fontSize: 14 }}>—</span>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  placeholder="max °C"
                  value={form.max}
                  onIonInput={(ev) => setForm((f) => ({ ...f, max: String(ev.detail.value ?? '') }))}
                  style={{
                    flex: 1, fontSize: 14, fontVariantNumeric: 'tabular-nums',
                    '--padding-start': '10px', '--padding-end': '10px',
                    '--padding-top': '8px', '--padding-bottom': '8px',
                    '--background': '#eef2f7', '--border-radius': '10px',
                  } as React.CSSProperties}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  Umidità (%)
                </div>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  placeholder="opzionale"
                  value={form.humidity}
                  onIonInput={(ev) => setForm((f) => ({ ...f, humidity: String(ev.detail.value ?? '') }))}
                  style={{
                    fontSize: 14,
                    '--padding-start': '10px', '--padding-end': '10px',
                    '--padding-top': '8px', '--padding-bottom': '8px',
                    '--background': '#eef2f7', '--border-radius': '10px',
                  } as React.CSSProperties}
                />
              </div>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🏷️ Identificazione (opzionale)
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Numero di serie</IonLabel>
                <IonInput
                  value={form.serial_number}
                  placeholder="es. SN-12345"
                  onIonInput={(ev) => setForm((f) => ({ ...f, serial_number: String(ev.detail.value ?? '') }))}
                />
                <IonButton slot="end" fill="clear" size="small" onClick={() => { void scanSerial(); }} style={{ '--padding-start': '8px', '--padding-end': '8px' } as React.CSSProperties}>
                  <IonIcon icon={barcodeOutline} slot="icon-only" />
                </IonButton>
              </IonItem>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              👥 Controllo operativo
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <PickerTriggerField
                  label="Assegna a"
                  placeholder="Tutto lo staff"
                  value={form.assigned_personnel_ids.length > 0
                    ? `${form.assigned_personnel_ids.length} assegnat${form.assigned_personnel_ids.length === 1 ? 'o' : 'i'}`
                    : undefined}
                  onClick={() => setStaffPickerOpen(true)}
                />
              </IonItem>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel>
                  <h3>Foto obbligatoria</h3>
                  <p>Richiede una foto del frigorifero prima di registrare il controllo.</p>
                </IonLabel>
                <IonToggle
                  slot="end"
                  checked={form.requires_photo}
                  onIonChange={(event) => setForm((value) => ({ ...value, requires_photo: !!event.detail.checked }))}
                />
              </IonItem>
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              <IonButton
                expand="block"
                onClick={() => void save()}
                style={{ '--border-radius': '12px', flex: 2, height: 46 } as React.CSSProperties}
              >
                {form.id ? 'Salva modifiche' : 'Crea frigorifero'}
              </IonButton>
              <IonButton
                expand="block"
                fill="outline"
                onClick={onDismiss}
                style={{ '--border-radius': '12px', flex: 1, height: 46 } as React.CSSProperties}
              >
                Annulla
              </IonButton>
            </div>
            {form.id && onRequestArchive && (
              <IonButton
                expand="block"
                color="danger"
                fill="clear"
                style={{ '--border-radius': '12px', marginTop: 8 } as React.CSSProperties}
                onClick={() => {
                  onRequestArchive({ id: form.id, name: form.name, version: form.version });
                  onDismiss();
                }}
              >
                Archivia frigorifero
              </IonButton>
            )}
          </div>
        </IonContent>
      </IonModal>
      <SearchPickerModal
        isOpen={staffPickerOpen}
        title="Assegna controllo"
        searchPlaceholder="Cerca persona…"
        multiple
        selectedIds={form.assigned_personnel_ids}
        emptyHint="Nessuna persona attiva nel MOD 05."
        items={personnel.filter((person) => person.attivo !== false && !person.archived_at).map((person) => ({ id: person.id, label: person.full_name, sublabel: person.role }))}
        onConfirm={(ids) => setForm((value) => ({ ...value, assigned_personnel_ids: ids }))}
        onClose={() => setStaffPickerOpen(false)}
      />
      <IonToast isOpen={!!toast} message={toast ?? ''} duration={1800} onDidDismiss={() => setToast(null)} />
    </>
  );
}
